import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import {
  Upload, FileText, CheckCircle, AlertTriangle, ArrowRight,
  Loader2, X, Building2, Zap, File, Camera
} from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#2A6FC4', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', bg: '#F5F7FA', saffron: '#FF9933' };

const STEPS = [
  { id: 'upload', label: 'Upload' },
  { id: 'extracting', label: 'Extracting' },
  { id: 'review', label: 'Review' },
  { id: 'submit', label: 'Quote & Bind' },
];

export default function DocumentUploadPage() {
  const api = useApi();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);   // <input capture="environment">: the phone's camera, one page per shot
  const isMobile = useMemo(() => (navigator.userAgentData?.mobile) || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent), []);
  const [justSnapped, setJustSnapped] = useState(false);
  const [step, setStep] = useState('upload');
  const [dragging, setDragging] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [pages, setPages] = useState([]);   // staged photos of ONE form, in page order, read together

  const isImage = (f) => /^image\//.test(f.type) || /\.(jpe?g|png|webp|heic|heif|tiff?)$/i.test(f.name);

  // Phone photos are 3–8 MB each; a 12-page set would blow the 25 MB limit and crawl on mobile data. Shrink to the
  // reader's own working size (longest side 1600 px, JPEG) before sending. Anything the browser cannot decode (HEIC on
  // some desktops) is sent as it is — the server reads it.
  const shrink = (file) => new Promise((resolve) => {
    if (!/^image\//.test(file.type) || file.size < 400 * 1024) return resolve(file);
    const img = new window.Image();   // the DOM constructor — `Image` here is the lucide icon
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      c.toBlob(b => { URL.revokeObjectURL(url); resolve(b ? new window.File([b], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }) : file); }, 'image/jpeg', 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

  const handleSnap = (list) => {
    const shot = Array.from(list || []).filter(isImage);
    if (!shot.length) return;
    const room = 12 - pages.length;
    if (room <= 0) { setError('12 pages is the most a form is read at once — read these first.'); return; }
    setError('');
    setPages(prev => [...prev, ...shot.slice(0, room)]);
    setJustSnapped(true);
  };
  useEffect(() => {
    if (!justSnapped) return undefined;
    const t = setTimeout(() => setJustSnapped(false), 8000);
    return () => clearTimeout(t);
  }, [justSnapped, pages.length]);

  const readFiles = useCallback(async (files) => {
    if (!files.length) return;
    setError('');
    setStep('extracting');
    setExtracting(true);

    const sent = await Promise.all(files.map(shrink));
    const formData = new FormData();
    if (sent.length === 1) formData.append('file', sent[0]);
    else sent.forEach(f => formData.append('files', f));

    try {
      const { data } = await api.post('/documents/upload-extract', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      setPages([]);
      setStep('review');
    } catch (err) {
      console.error('Upload error:', err?.message);
      setError(err?.response?.data?.detail || 'Failed to process document. Please try a different file.');
      setStep('upload');
    }
    setExtracting(false);
  }, [api]);

  // Photos are staged so the broker can add page 2, page 3… before the form is read as one document;
  // anything else (PDF, email, spreadsheet) is read straight away.
  const handleFiles = useCallback((list) => {
    const files = Array.from(list || []).filter(Boolean);
    if (!files.length) return;
    const photos = files.filter(isImage);
    if (photos.length === files.length) {
      const room = 12 - pages.length;
      setError(photos.length > room ? `Only ${room} more photo${room === 1 ? '' : 's'} fit — a form is read 12 pages at a time; the rest were not added.` : '');
      setPages(prev => [...prev, ...photos.slice(0, Math.max(room, 0))]);
      return;
    }
    if (pages.length) { setError('Finish the photo set first (read it or clear it), then upload the other document.'); return; }
    if (files.length > 1) setError(`Only ${files[0].name} was read — documents other than photos are read one at a time.`);
    readFiles([files[0]]);
  }, [pages.length, readFiles]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer?.files);
  }, [handleFiles]);

  const thumbs = useMemo(() => pages.map(f => URL.createObjectURL(f)), [pages]);
  useEffect(() => () => thumbs.forEach(u => URL.revokeObjectURL(u)), [thumbs]);

  const movePage = (i, dir) => setPages(prev => {
    const next = [...prev]; const j = i + dir;
    if (j < 0 || j >= next.length) return prev;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  // The upload never becomes a submission on its own: it pre-fills Quote & Bind, where the same engine as every
  // other quote decides, prices and binds it — so there is always a next step to finalise.
  const continueToQuote = () => {
    if (!result?.upload_id) return;
    navigate(`/new-business?tab=quote&upload=${result.upload_id}`);
  };

  const reset = () => {
    setStep('upload');
    setResult(null);
    setPages([]);
    setError('');
  };

  return (
    <div className="space-y-5" data-testid="document-upload-page">
      {/* Progress Steps */}
      <div className="flex items-center gap-2 mb-2">
        {STEPS.map((s, i) => {
          const stepIdx = STEPS.findIndex(st => st.id === step);
          const isDone = i < stepIdx;
          const isActive = s.id === step;
          return (
            <React.Fragment key={s.id}>
              {i > 0 && <div className="flex-1 h-px" style={{ background: isDone ? C.green : 'rgba(136,150,168,0.2)' }} />}
              <div className="flex items-center gap-1.5">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${isDone ? 'text-white' : isActive ? 'text-white' : ''}`}
                  style={{ background: isDone ? C.green : isActive ? C.teal : 'rgba(136,150,168,0.15)', color: !isDone && !isActive ? C.slate : undefined }}>
                  {isDone ? <CheckCircle className="w-3.5 h-3.5" /> : i + 1}
                </div>
                <span className="text-xs font-medium hidden sm:inline" style={{ color: isActive ? C.navy : C.slate }}>{s.label}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Step 1: Upload / Drop Zone */}
      {/* Hidden pickers live OUTSIDE the drop zone: a programmatic click on them must not bubble to the zone's onClick
          (that opened the gallery picker as well as the camera — iteration 155). */}
      <input ref={fileRef} type="file" className="hidden" multiple
        accept=".pdf,.csv,.xlsx,.txt,.eml,.jpg,.jpeg,.png,.webp,.heic,.heif,.tif,.tiff,image/*"
        onChange={e => { const list = e.target.files; const arr = Array.from(list || []); e.target.value = ''; handleFiles(arr); }} />
      <input ref={cameraRef} type="file" className="hidden" accept="image/*" capture="environment" data-testid="camera-input"
        onChange={e => { const list = e.target.files; const arr = Array.from(list || []); e.target.value = ''; handleSnap(arr); }} />

      {step === 'upload' && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all ${dragging ? 'border-[#00B4D8] bg-[rgba(0,180,216,0.04)]' : 'border-[rgba(136,150,168,0.25)] hover:border-[rgba(136,150,168,0.5)]'}`}
          data-testid="drop-zone"
        >
          <Upload className="w-10 h-10 mx-auto mb-4" style={{ color: dragging ? C.teal : C.slate }} />
          <p className="text-sm font-medium mb-1" style={{ color: C.navy }}>
            {dragging ? 'Drop your document here' : isMobile ? 'Tap to choose a document' : 'Drag and drop a document here'}
          </p>
          <p className="text-xs" style={{ color: C.slate }}>
            {isMobile ? 'PDF, photos of the form, email or spreadsheet' : 'or click to browse — PDF (typed or scanned), photos of the form (several pages at once), email, CSV, XLSX, TXT'}
          </p>
          <button type="button" onClick={e => { e.stopPropagation(); cameraRef.current?.click(); }}
            className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold text-white"
            style={{ background: C.navy }} data-testid="camera-btn">
            <Camera className="w-4 h-4" /> {isMobile ? 'Photograph the form' : 'Use the camera'}
          </button>
          <p className="text-[11px] mt-2" style={{ color: C.slate }}>One page per shot — snap page 1, then page 2… and read them together.</p>
          <div className="flex items-center justify-center gap-4 mt-6">
            {['Proposal Form', 'Scanned PDF', 'Phone Photo', 'Email', 'Spreadsheet'].map(t => (
              <span key={t} className="text-[10px] px-2 py-1 rounded-full" style={{ background: C.bg, color: C.slate }}>{t}</span>
            ))}
          </div>
        </div>
      )}

      {step === 'upload' && pages.length > 0 && (
        <Card className="p-4 rounded-lg" style={{ border: '1px solid rgba(0,180,216,0.3)' }} data-testid="photo-pages-card">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <File className="w-4 h-4" style={{ color: C.teal }} />
            <h3 className="text-sm font-semibold" style={{ color: C.navy }}>One form, {pages.length} photo{pages.length === 1 ? '' : 's'}</h3>
            <span className="text-[11px]" style={{ color: C.slate }}>Put them in page order — they are read together as one document (up to 12).</span>
            {justSnapped && (
              <span className="text-[11px] font-semibold" style={{ color: C.green }} data-testid="snap-confirmation">
                Page {pages.length} captured — snap page {pages.length + 1}, or read the form.
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-3 mb-4">
            {pages.map((f, i) => (
              <div key={`${f.name}-${i}`} className="relative w-28 sm:w-36 rounded-md overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.25)', background: C.bg }} data-testid={`photo-page-${i + 1}`}>
                <img src={thumbs[i]} alt={`Page ${i + 1}`} className="w-full h-36 sm:h-44 object-cover" />
                <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-white" style={{ background: C.navy }}>Page {i + 1}</div>
                <div className="flex items-center justify-between px-1.5 py-1 text-[10px]" style={{ color: C.slate }}>
                  <button onClick={() => movePage(i, -1)} disabled={i === 0} className="disabled:opacity-30 px-1" data-testid={`photo-page-${i + 1}-up`} title="Move earlier">◀</button>
                  <span className="truncate max-w-[70px]" title={f.name}>{f.name}</span>
                  <button onClick={() => movePage(i, 1)} disabled={i === pages.length - 1} className="disabled:opacity-30 px-1" data-testid={`photo-page-${i + 1}-down`} title="Move later">▶</button>
                </div>
                <button onClick={() => setPages(prev => prev.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-white" style={{ background: C.red }} data-testid={`photo-page-${i + 1}-remove`} title="Remove"><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => cameraRef.current?.click()} className="flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold text-white" style={{ background: C.navy }} data-testid="snap-next-btn">
              <Camera className="w-4 h-4" /> Snap page {pages.length + 1}
            </button>
            <button onClick={() => readFiles(pages)} className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold text-white" style={{ background: C.teal }} data-testid="read-pages-btn">
              <Zap className="w-4 h-4" /> Read {pages.length} page{pages.length === 1 ? '' : 's'}
            </button>
            <button onClick={() => fileRef.current?.click()} className="px-4 py-2.5 rounded-md text-sm font-medium" style={{ color: C.navy, border: '1px solid rgba(136,150,168,0.3)' }} data-testid="add-photo-btn">
              + Add from gallery
            </button>
            <button onClick={() => { setPages([]); setError(''); }} className="px-4 py-2.5 rounded-md text-sm font-medium" style={{ color: C.slate }} data-testid="clear-pages-btn">Clear</button>
          </div>
        </Card>
      )}

      {/* Step 2: Extracting */}
      {step === 'extracting' && (
        <Card className="p-12 text-center rounded-lg" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
          <Loader2 className="w-10 h-10 mx-auto mb-4 animate-spin" style={{ color: C.teal }} />
          <p className="text-sm font-medium" style={{ color: C.navy }}>Reading the document…</p>
          <p className="text-xs mt-1" style={{ color: C.slate }}>Scans and photos are read page by page (a few seconds each), then the fields are extracted and the client matched</p>
        </Card>
      )}

      {/* Step 3: Review extracted data */}
      {step === 'review' && result && (
        <div className="space-y-4">
          {result.read?.mode && result.read.mode !== 'text' && (
            <div className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg text-xs" data-testid="upload-read-mode"
              style={{ background: result.low_confidence ? 'rgba(232,160,32,0.08)' : 'rgba(0,180,216,0.06)', border: `1px solid ${result.low_confidence ? 'rgba(232,160,32,0.35)' : 'rgba(0,180,216,0.25)'}`, color: C.navy }}>
              <Zap className="w-3.5 h-3.5" style={{ color: result.low_confidence ? C.amber : C.teal }} />
              <span>
                Read from {result.read.mode === 'photo' ? (result.read.parts > 1 ? <b>{result.read.parts} photos of one form</b> : <b>a photo</b>) : <b>a scanned PDF</b>}
                {result.read.pages_total > 1 ? ` — ${result.read.pages_read} of ${result.read.pages_total} pages read` : ''}
                {result.read.pages_total > result.read.pages_read ? ' (the rest were not read — the form is longer than 12 pages)' : ''}.
              </span>
              {result.low_confidence && (
                <span className="font-semibold" style={{ color: '#B27516' }} data-testid="upload-low-confidence">
                  Low confidence — the image was hard to read; check every figure against the form before quoting.
                </span>
              )}
            </div>
          )}
          {/* Entity Match */}
          <Card className="p-4 rounded-lg" style={{ border: `1px solid ${result.entity_match?.confidence === 'high' ? 'rgba(29,158,117,0.3)' : result.entity_match?.confidence === 'medium' ? 'rgba(232,160,32,0.3)' : 'rgba(136,150,168,0.2)'}` }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: result.entity_match?.entity_id ? 'rgba(29,158,117,0.1)' : 'rgba(232,160,32,0.1)' }}>
                <Building2 className="w-4 h-4" style={{ color: result.entity_match?.entity_id ? C.green : C.amber }} />
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: C.navy }}>
                  {result.entity_match?.entity_id ? `Matched: ${result.entity_match.legal_name}` : 'No entity match found'}
                </p>
                <p className="text-[11px]" style={{ color: C.slate }}>
                  {result.entity_match?.confidence === 'high' ? 'ABN match — high confidence' : result.entity_match?.confidence === 'medium' ? 'Name match — verify details' : 'No client on file yet — Quote & Bind opens with the client form pre-filled'}
                </p>
              </div>
            </div>
          </Card>

          {/* Extracted Fields */}
          <Card className="p-4 rounded-lg" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4" style={{ color: C.teal }} />
              <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Extracted Fields</h3>
              <span className="text-[10px] px-2 py-0.5 rounded-full capitalize" style={{ background: C.teal + '12', color: C.teal }}>{result.document_type?.replace(/_/g, ' ')}</span>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {Object.entries(result.extracted_fields || {}).filter(([k, v]) => v !== null && v !== '').map(([key, value]) => (
                <div key={key} className="flex items-center justify-between py-1.5 border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                  <span className="text-[11px] capitalize" style={{ color: C.slate }}>{key.replace(/_/g, ' ')}</span>
                  <span className="text-xs font-medium" style={{ color: C.navy }}>{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Missing Fields Warning */}
          {result.missing_fields?.length > 0 && (
            <Card className="p-3 rounded-lg" style={{ border: '1px solid rgba(232,160,32,0.2)', background: 'rgba(232,160,32,0.04)' }}>
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" style={{ color: C.amber }} />
                <p className="text-xs font-medium" style={{ color: C.amber }}>
                  Missing: {result.missing_fields.map(f => f.replace(/_/g, ' ')).join(', ')}
                </p>
              </div>
            </Card>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button onClick={continueToQuote}
              className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold text-white"
              style={{ background: C.teal }}
              data-testid="submit-extracted">
              <ArrowRight className="w-4 h-4" />
              Continue in Quote & Bind
            </button>
            <button onClick={reset} data-testid="upload-reset" className="px-4 py-2.5 rounded-md text-sm font-medium" style={{ color: C.slate, border: '1px solid rgba(136,150,168,0.2)' }}>
              Upload Different File
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <Card className="p-3 rounded-lg" data-testid="upload-error" style={{ border: '1px solid rgba(226,75,74,0.2)', background: 'rgba(226,75,74,0.04)' }}>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" style={{ color: C.red }} />
            <p className="text-xs font-medium" style={{ color: C.red }}>{error}</p>
          </div>
        </Card>
      )}
    </div>
  );
}
